import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { signalsData } from "Stores/signalsData";
import AgentSelect from "./AgentSelect";
import { triggerEvents } from "./triggerCatalog";
import { familyOf } from "./templateCatalog";
import { isAlwaysOn, alwaysOnValue, savedOrAlwaysOn } from "../signals/signalMeta";
import { signalsOn } from "../signals/signalsGate";
import "./agentTemplates.scss";

// How an agent starts. Value is the builder's trigger kind.
export const START_KINDS = [
  { value: "signal", icon: "wifi_tethering", label: "From your Signals", hint: "A change outside, from your Signals feed" },
  { value: "event", icon: "flash_on", label: "When it happens in Salesgear", hint: "A reply, a click, a bounce, a call" },
  { value: "schedule", icon: "schedule", label: "On a schedule", hint: "Every day, weekdays or set days" },
  { value: "manual", icon: "play_circle_outline", label: "When you run it", hint: "Only when you press Run" }
];

/** Items of the Signals catalog that are signals (a missing kind counts as a signal). */
export const signalCatalog = catalog =>
  (catalog || []).filter(d => String(d.kind || "signal").toLowerCase() === "signal");

const matchesQuery = (q, ...texts) => !q || texts.join(" ").toLowerCase().includes(q);

/**
 * Pick a signal from the Signals catalog. An agent listens to one of your tracked signals, so picking a
 * type selects your tracked signal of that type (or asks which one, or points to Signals to track it).
 */
export const SignalTriggerPicker = observer(({ value, onChange, suggestedType = "", onPickType }) => {
  const history = useHistory();
  const [q, setQ] = useState("");
  const savedFor = type => signalsData.saved.filter(s => (s.signal || {}).signalType === type);
  const current = savedOrAlwaysOn(signalsData.saved, signalsData.catalog, value);
  // suggestedType: a described agent named the signal ("champion changes jobs") but they don't track it yet.
  const [type, setType] = useState(current ? current.signal.signalType : suggestedType);
  const listRef = useRef(null);

  useEffect(() => {
    if (!signalsData.catalog.length) signalsData.fetchCatalog();
    if (!signalsData.saved.length) signalsData.fetchSaved();
  }, []);
  // A saved value loads after mount: show its type as picked.
  useEffect(() => { if (current && !type) setType(current.signal.signalType); }, [current, type]);
  // Tracking happens in Signals (a new tab, so a draft here isn't lost): refresh when they come back,
  // and pick the signal once they track one of the type they chose.
  useEffect(() => {
    const refresh = () => { if (!document.hidden) signalsData.fetchSaved(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, []);
  const trackedOfType = type && type !== "custom" ? savedFor(type) : [];
  useEffect(() => {
    if (!value && trackedOfType.length === 1) onChange(trackedOfType[0].signal.id);
  }, [value, trackedOfType.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = q.trim().toLowerCase();
  const items = signalCatalog(signalsData.catalog).filter(d => matchesQuery(query, d.label, d.description, d.category));
  // A free signal is always on: the server made its saved signal (alwaysOnSignalId), nothing to create here.
  const pick = d => {
    setType(d.id);
    if (onPickType) onPickType(d.id);
    const mine = savedFor(d.id);
    if (!mine.length && isAlwaysOn(d)) onChange(alwaysOnValue(d));
    else onChange(mine.length === 1 ? mine[0].signal.id : "");
  };
  // Custom signals the team saved: each one is its own trigger, picked directly.
  const customs = signalsData.saved.filter(s => (s.signal || {}).signalType === "custom" && matchesQuery(query, s.displayName, ((s.custom || {}).definition || {}).condition, "custom"));
  const pickCustom = row => {
    setType("custom");
    if (onPickType) onPickType("custom");
    onChange(row.signal.id);
  };
  const typeDef = signalsData.catalog.find(d => d.id === type);
  const tracked = type && type !== "custom" ? savedFor(type) : [];
  // A preselected signal can sit below the fold of the list: bring it into view (the list only, not the page).
  const catalogReady = signalsData.catalog.length > 0;
  useEffect(() => {
    const list = listRef.current;
    const row = list && list.querySelector(".is-on");
    if (row) list.scrollTop = row.offsetTop - list.offsetTop - 8;
  }, [catalogReady]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="agt-picker">
      <label className="agt-search agt-search--full">
        <i className="material-icons-outlined">search</i>
        <input aria-label="Search signals" placeholder="Search signals, e.g. job change" value={q} onChange={e => setQ(e.target.value)} />
      </label>
      <div className="agt-picker__list" role="listbox" aria-label="Signals" ref={listRef}>
        {customs.map(row => (
          <button type="button" role="option" aria-selected={value === row.signal.id} key={row.signal.id}
            className={`agt-picker__row${value === row.signal.id ? " is-on" : ""}`} onClick={() => pickCustom(row)}>
            <span className="agt-tile agt-tile--sm agt-tile--account"><i className="material-icons-outlined">wifi_tethering</i></span>
            <span className="agt-picker__text">
              <b>{row.displayName}</b>
              <span>Your custom signal · {row.scopeLabel || "your list"}</span>
            </span>
            <span className="agt-picker__meta">{(row.signal || {}).status === "PAUSED" ? "Paused" : "Tracking"}</span>
          </button>
        ))}
        {items.length === 0 && customs.length === 0 && <div className="agt-picker__empty">{signalsData.catalog.length ? "No signals match" : "Loading signals…"}</div>}
        {items.map(d => (
          <button type="button" role="option" aria-selected={type === d.id} key={d.id}
            className={`agt-picker__row${type === d.id ? " is-on" : ""}`} onClick={() => pick(d)}>
            <span className={`agt-tile agt-tile--sm agt-tile--${familyOf(d.category)}`}><i className="material-icons-outlined">wifi_tethering</i></span>
            <span className="agt-picker__text">
              <b>{d.label}</b>
              {d.description && <span>{d.description}</span>}
            </span>
            {(savedFor(d.id).length > 0 || isAlwaysOn(d)) && <span className="agt-picker__meta">{savedFor(d.id).length ? "Tracking" : "On"}</span>}
          </button>
        ))}
      </div>
      {type && type !== "custom" && tracked.length === 0 && !isAlwaysOn(typeDef) && (
        <span className="ag-hint">
          You don't track this signal yet. <a className="ag-link" href={history.createHref({ pathname: "/signals", search: "?tab=discover" })} target="_blank" rel="noopener noreferrer">Track it in Signals</a> (opens a new tab). It shows up here once you do.
        </span>
      )}
      {tracked.length > 1 && (
        <label className="ag-field">
          <span className="ag-field__label">Which one?</span>
          <AgentSelect value={value} placeholder="Choose a tracked signal…" onChange={onChange}
            options={tracked.map(s => ({ value: s.signal.id, label: s.displayName }))} />
        </label>
      )}
    </div>
  );
});

/** Pick an event inside Salesgear from the trigger catalog. */
export const EventTriggerPicker = ({ value, onChange }) => {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const items = triggerEvents().filter(e => matchesQuery(query, e.label));
  return (
    <div className="agt-picker">
      <label className="agt-search agt-search--full">
        <i className="material-icons-outlined">search</i>
        <input aria-label="Search events" placeholder="Search events, e.g. reply, bounce" value={q} onChange={e => setQ(e.target.value)} />
      </label>
      <div className="agt-picker__list" role="listbox" aria-label="Events">
        {items.length === 0 && <div className="agt-picker__empty">No events match</div>}
        {items.map(e => (
          <button type="button" role="option" aria-selected={value === e.value} key={e.value}
            className={`agt-picker__row${value === e.value ? " is-on" : ""}`} onClick={() => onChange(e.value)}>
            <span className="agt-tile agt-tile--sm agt-tile--plain"><i className="material-icons-outlined">flash_on</i></span>
            <span className="agt-picker__text"><b>{e.label}</b></span>
          </button>
        ))}
      </div>
    </div>
  );
};

/** The first choice in the builder: how the agent starts. */
export const StartKindChooser = ({ value, onChange }) => (
  <div className="agt-kinds" role="radiogroup" aria-label="How it starts">
    {START_KINDS.filter(k => k.value !== "signal" || signalsOn() || value === "signal").map(k => (
      <button type="button" role="radio" aria-checked={value === k.value} key={k.value}
        className={`agt-kind${value === k.value ? " is-on" : ""}`} onClick={() => onChange(k.value)}>
        <i className="material-icons-outlined">{k.icon}</i>
        <span className="agt-kind__text"><b>{k.label}</b><span>{k.hint}</span></span>
      </button>
    ))}
  </div>
);
